import { 
    Box, 
    Button, 
    FormControl, 
    Modal, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TablePagination, 
    TableRow, 
    TableSortLabel, 
    TextField 
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { editModalStyle, ParticipantDirectoryInterface, tableDesign } from '../app/consts';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { getDirectoryAsync, selectDirectory, updateDirectoryAsync } from '../features/ParticipantDirectory/ParticipantDirectorySlice';
import { toast } from 'react-toastify';
import { selectEditFlag, setEditFlag } from '../features/add_workout/workoutSlice';

type Order = 'asc' | 'desc';
type SortKey = keyof ParticipantDirectoryInterface;

interface HeadCell {
    id: SortKey;
    label: string;
    sortable: boolean;
}

const headCells: HeadCell[] = [
    { id: 'first_name', label: 'שם פרטי', sortable: true },
    { id: 'last_name', label: 'שם משפחה', sortable: true },
    { id: 'email', label: 'דואר אלקטרוני', sortable: true },
    { id: 'phone_number', label: 'מספר טלפון', sortable: true },
    { id: 'first_registered_at', label: 'תאריך רישום ראשון', sortable: true },
    { id: 'last_registered_at', label: 'תאריך רישום אחרון', sortable: true },
    { id: 'total_registrations', label: 'כמות אימונים', sortable: true },
    { id: 'admin_notes', label: 'הערות מנהל', sortable: false },
];

const ParticipantDirectory = () => {
    const dispatch = useAppDispatch();
    const directory = useAppSelector(selectDirectory);
    const editFlag = useAppSelector(selectEditFlag);
    const [editModalShow, setEditModalShow] = useState(false);
    const [workoutsModalShow, setWorkoutsModalShow] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(6);

    // Sorting state
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<SortKey>('first_name');

    const [toUpdate, setToUpdate] = useState<ParticipantDirectoryInterface>();
    const [note, setNote] = useState("");

    const handleRequestSort = (property: SortKey) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    useEffect(() => {
        dispatch(getDirectoryAsync());
        // eslint-disable-next-line
    }, [editFlag]);

    useEffect(() => {
        if (toUpdate && directory.length > 0) {
            const freshUser = directory.find((user) => user.id === toUpdate.id);
            if (freshUser) {
                setToUpdate(freshUser);
            }
        }
        // eslint-disable-next-line
    }, [directory]);

    const handleRemoveManagerNote = (id: number) => {
        if (!toUpdate) return;
        const updated = { ...toUpdate, admin_notes: toUpdate.admin_notes.filter((value) => value.id !== id) };
        dispatch(updateDirectoryAsync(updated)).unwrap()
            .then(() => {
                dispatch(setEditFlag());
                toast.success("ההערה נמחקה בהצלחה");
                setNote("");
            })
            .catch(() => {
                toast.error("אירעה שגיאה");
            });
    };

    const handleAddManagerNote = () => {
        if (!toUpdate) return;
        const updated = { ...toUpdate, admin_notes: [...toUpdate.admin_notes, { id: toUpdate.admin_notes.length + 1, content: note }] };
        dispatch(updateDirectoryAsync(updated)).unwrap()
            .then(() => {
                dispatch(setEditFlag());
                toast.success("ההערה התווספה בהצלחה");
                setNote("");
            })
            .catch(() => {
                toast.error("אירעה שגיאה");
            });
    };

    const visibleRows = useMemo(() => {
        if (!directory) return [];

        const comparator = (a: ParticipantDirectoryInterface, b: ParticipantDirectoryInterface) => {
            let valA = a[orderBy];
            let valB = b[orderBy];

            if (orderBy === 'first_registered_at' || orderBy === 'last_registered_at') {
                valA = new Date(valA as string).getTime();
                valB = new Date(valB as string).getTime();
            }

            if (valB < valA) return -1;
            if (valB > valA) return 1;
            return 0;
        };

        const sorted = [...directory].sort((a, b) => 
            order === 'desc' ? comparator(a, b) : -comparator(a, b)
        );

        return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [directory, order, orderBy, page, rowsPerPage]);

    return (
        <>
            <div style={{ margin: 'auto', textAlign: 'center', marginTop: '2%' }}>
                <h1>ניהול מתאמנים</h1>

                {directory.length === 0 ? (
                    <img
                        src='/FadingCircles.gif'
                        alt='spinner'
                        style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
                    />
                ) : (
                    <Paper sx={{ ...tableDesign }}>
                        <TableContainer sx={{ maxHeight: "65dvh" }}>
                            <Table
                                stickyHeader
                                aria-label="sticky table"
                                sx={{ "& .MuiTableCell-root": { fontSize: "1em", textAlign: 'center' } }}>
                                <TableHead>
                                    <TableRow>
                                        {headCells.map((headCell) => (
                                            <TableCell
                                                key={headCell.id}
                                                sortDirection={orderBy === headCell.id ? order : false}>
                                                {headCell.sortable ? (
                                                    <TableSortLabel
                                                        active={orderBy === headCell.id}
                                                        direction={orderBy === headCell.id ? order : 'asc'}
                                                        onClick={() => handleRequestSort(headCell.id)}>
                                                        {headCell.label}
                                                    </TableSortLabel>
                                                ) : (
                                                    headCell.label
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleRows.map((user: ParticipantDirectoryInterface) => (
                                        <TableRow key={user.id}>
                                            <TableCell>{user.first_name}</TableCell>
                                            <TableCell>{user.last_name}</TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>{user.phone_number}</TableCell>
                                            <TableCell>{new Date(user.first_registered_at).toLocaleString()}</TableCell>
                                            <TableCell>{new Date(user.last_registered_at).toLocaleString()}</TableCell>
                                            <TableCell>
                                                {user.total_registrations}<br />
                                                <Button 
                                                    variant="text" 
                                                    size='small' 
                                                    color='info' 
                                                    onClick={() => { setWorkoutsModalShow(true); setToUpdate(user); }}>
                                                    לחץ לצפייה
                                                </Button>
                                            </TableCell>
                                            <TableCell>
                                                <span style={{ color: user.admin_notes?.length === 0 ? "gray" : "green" }}>
                                                    {user.admin_notes?.length === 0 ? "אין הערות" : "קיימות הערות"}
                                                </span>
                                                <br />
                                                <Button 
                                                    variant="text" 
                                                    size='small' 
                                                    color='secondary' 
                                                    onClick={() => { setEditModalShow(true); setToUpdate(user); }}>
                                                    לחץ להזנה וצפייה
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[3, 6, 12]}
                            component="div"
                            count={directory.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ justifySelf: 'right', direction: 'ltr' }}/>
                    </Paper>
                )}
                <Modal open={editModalShow} onClose={() => setEditModalShow(false)}>
                    <Box sx={editModalStyle}>
                        <p style={{ fontSize: '20px', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => setEditModalShow(false)}>X</p>
                        <h3>הערות מנהל עבור המתאמן:<br />{toUpdate?.first_name} {toUpdate?.last_name}</h3>
                        {toUpdate?.admin_notes?.map((note) => (
                            <div key={note.id}>
                                <Button onClick={() => handleRemoveManagerNote(note.id)}>🗑</Button>
                                {note.content}
                            </div>
                        ))}
                        <br />
                        <form method='POST' id='directoryNotesForm' onSubmit={(e) => { e.preventDefault(); handleAddManagerNote(); }}>
                            <FormControl>
                                <TextField required variant="filled" label='הערות' type='text' value={note} onChange={(e) => setNote(e.target.value)} style={{ textAlign: 'center', width: '300px' }} slotProps={{ htmlInput: { pattern: ".+", required: true } }} />
                                <Button id='submit' type='submit' variant="contained" size='large' color='success'>הוסף הערה</Button>
                            </FormControl>
                        </form>
                    </Box>
                </Modal>
                <Modal open={workoutsModalShow} onClose={() => setWorkoutsModalShow(false)}>
                    <Box sx={{ ...editModalStyle, height: "auto" }}>
                        <p style={{ fontSize: '20px', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => setWorkoutsModalShow(false)}>X</p>
                        {toUpdate?.workouts_list?.map((value) => (
                            <p key={value.id}>
                                אימון {value.type} ({value.id}) - {value.date} - {value.time.slice(0, 5)}
                            </p>
                        ))}
                    </Box>
                </Modal>
            </div>
        </>
    );
};

export default ParticipantDirectory;